import React, { useReducer, useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Play, Pause, CheckCircle2, XCircle,
  Loader2, AlertCircle, Building2, ExternalLink,
} from 'lucide-react';

import { Button, Card, Badge } from '../../ui-v2';
import {
  wizardReducer, initialState, buildPayload,
} from '../wizard/wizardState';
import {
  getCampanaById, updateCampana, cambiarEstadoCampana, previewImpacto,
} from '../api';

import Paso1Info from '../wizard/Paso1Info';
import Paso2Subtareas from '../wizard/Paso2Subtareas';
import Paso3Fechas from '../wizard/Paso3Fechas';
import Paso4DiasDisponibles from '../wizard/Paso4DiasDisponibles';
import Paso5Tecnicos from '../wizard/Paso5Tecnicos';
import UsuariosEnLotes from '../lotes/UsuariosEnLotes';
import ModalImpacto from '../components/ModalImpacto';

const BADGE_VARIANT = {
  Activa: 'success',
  Borrador: 'secondary',
  Pausada: 'warning',
  Completada: 'accent',
  Cancelada: 'destructive',
};

export default function EditarCampana() {
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const id = parseInt(idParam, 10);

  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const [campanaOriginal, setCampanaOriginal] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [cargaError, setCargaError] = useState(null);

  const [guardando, setGuardando] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState(null);
  const [mensajeOk, setMensajeOk] = useState(null);

  // Modal de impacto
  const [impactoModal, setImpactoModal] = useState(null);

  // Modal de transición de estado
  const [transicionModal, setTransicionModal] = useState(null);
  const [transicionLoading, setTransicionLoading] = useState(false);

  // --- Carga inicial ---

  useEffect(() => {
    if (!id) return;
    setCargando(true);
    setCargaError(null);
    getCampanaById(id)
      .then((c) => {
        if (c.estado === 'Borrador') {
          navigate(`/citas-tecnicas/campanas/${c.id}/wizard`, { replace: true });
          return;
        }
        if (c.estado === 'Completada' || c.estado === 'Cancelada') {
          setCargaError(`Esta campaña está ${c.estado.toLowerCase()} y no puede editarse.`);
          return;
        }
        setCampanaOriginal(c);
        dispatch({ type: 'LOAD', campana: c });
      })
      .catch((e) => {
        console.error('[EditarCampana]', e);
        setCargaError(
          e?.response?.status === 404 ? 'La campaña no existe.' : 'No se pudo cargar la campaña.'
        );
      })
      .finally(() => setCargando(false));
  }, [id, navigate]);

  // --- Guardar cambios ---

  const intentarGuardar = useCallback(async () => {
    setErrorGuardado(null);
    setMensajeOk(null);
    setGuardando(true);
    try {
      const payload = buildPayload(state, {});
      const impacto = await previewImpacto(id, payload);
      if (impacto.tiene_impacto) {
        setImpactoModal(impacto);
        setGuardando(false);
        return;
      }
      await ejecutarGuardado(payload, false);
    } catch (e) {
      console.error('[intentarGuardar]', e);
      setErrorGuardado(e?.response?.data?.error || 'No se pudo evaluar el impacto.');
      setGuardando(false);
    }
  }, [id, state]);

  async function ejecutarGuardado(payload, confirmarImpacto) {
    setGuardando(true);
    setErrorGuardado(null);
    try {
      await updateCampana(id, { ...payload, confirmar_impacto: confirmarImpacto });
      const c = await getCampanaById(id);
      setCampanaOriginal(c);
      dispatch({ type: 'LOAD', campana: c });
      setImpactoModal(null);
      setMensajeOk('Cambios guardados.');
      setTimeout(() => setMensajeOk(null), 2500);
    } catch (e) {
      console.error('[ejecutarGuardado]', e);
      const errs = e?.response?.data?.errores;
      if (Array.isArray(errs) && errs.length) {
        dispatch({ type: 'SET_ERRORES', errores: errs });
        setImpactoModal(null);
        setErrorGuardado('Hay errores de validación. Revisa los campos resaltados.');
      } else {
        setErrorGuardado(e?.response?.data?.error || 'No se pudo guardar la campaña.');
      }
    } finally {
      setGuardando(false);
    }
  }

  function confirmarImpacto() {
    const payload = buildPayload(state, {});
    return ejecutarGuardado(payload, true);
  }

  // --- Transición de estado ---

  function pedirTransicion(nuevoEstado) {
    setTransicionModal({ nuevoEstado });
  }

  async function ejecutarTransicion() {
    if (!transicionModal) return;
    setTransicionLoading(true);
    try {
      await cambiarEstadoCampana(id, transicionModal.nuevoEstado);
      const c = await getCampanaById(id);
      setCampanaOriginal(c);
      if (c.estado === 'Completada' || c.estado === 'Cancelada') {
        navigate('/citas-tecnicas/campanas');
        return;
      }
      dispatch({ type: 'LOAD', campana: c });
      setTransicionModal(null);
    } catch (e) {
      console.error('[ejecutarTransicion]', e);
      const msg = e?.response?.data?.error || 'No se pudo cambiar el estado.';
      setTransicionModal((m) => ({ ...m, error: msg }));
    } finally {
      setTransicionLoading(false);
    }
  }

  // --- Render ---

  if (cargando) {
    return (
      <div className="px-6 py-12 text-center text-slate-500 text-sm">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
        Cargando campaña…
      </div>
    );
  }
  if (cargaError) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto">
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-red-700 text-sm">
          {cargaError}
        </div>
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/citas-tecnicas/campanas')}>
            <ArrowLeft className="w-4 h-4" />
            Volver a la lista
          </Button>
        </div>
      </div>
    );
  }
  if (!campanaOriginal) return null;

  const estadoActual = campanaOriginal.estado;
  const transiciones = TRANSICIONES_UI[estadoActual] || [];

  return (
    <div className="min-h-full">
      {/* Header sticky */}
      <div className="sticky top-14 z-30 bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate('/citas-tecnicas/campanas')}
              className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-1"
            >
              <ArrowLeft className="w-3 h-3" /> Volver a la lista
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-lg font-semibold text-slate-900 truncate max-w-md">
                {campanaOriginal.titulo}
              </h1>
              <Badge variant={BADGE_VARIANT[estadoActual] || 'secondary'}>
                {estadoActual}
              </Badge>
              <span className="text-xs text-slate-500 flex items-center gap-1">
                {campanaOriginal.ambito === 'colaborador' ? (
                  <ExternalLink className="w-3.5 h-3.5" />
                ) : (
                  <Building2 className="w-3.5 h-3.5" />
                )}
                {campanaOriginal.ambito === 'colaborador' ? 'Colaboradores' : 'Internos'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {transiciones.map((t) => (
              <Button
                key={t.estado}
                variant={t.variant || 'outline'}
                size="sm"
                onClick={() => pedirTransicion(t.estado)}
                disabled={guardando || transicionLoading}
              >
                {t.icon}
                {t.label}
              </Button>
            ))}
            <Button onClick={intentarGuardar} disabled={guardando}>
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar cambios
            </Button>
          </div>
        </div>

        {(errorGuardado || mensajeOk) && (
          <div className="mt-2">
            {errorGuardado && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-2.5 py-1.5 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                {errorGuardado}
              </div>
            )}
            {mensajeOk && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2.5 py-1.5 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {mensajeOk}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cuerpo: secciones */}
      <div className="px-6 py-5 space-y-6 max-w-5xl">
        <Seccion titulo="Información">
          <NotaModoEdicion>
            El <strong>ámbito</strong> ({campanaOriginal.ambito === 'colaborador' ? 'Colaboradores' : 'Internos'}) no se puede cambiar una vez la campaña está activa.
          </NotaModoEdicion>
          <Paso1Info
            payload={state.payload}
            errores={state.errores}
            dispatch={(action) => {
              // Bloquear cambio de ámbito en edición
              if (action.type === 'CAMBIAR_AMBITO') return;
              dispatch(action);
            }}
          />
        </Seccion>

        <Seccion titulo="Subtareas">
          <Paso2Subtareas payload={state.payload} errores={state.errores} dispatch={dispatch} />
        </Seccion>

        <Seccion titulo="Fechas y horario">
          <Paso3Fechas payload={state.payload} errores={state.errores} dispatch={dispatch} />
        </Seccion>

        <Seccion titulo="Días disponibles">
          <Paso4DiasDisponibles payload={state.payload} dispatch={dispatch} />
        </Seccion>

        <Seccion titulo="Técnicos">
          <Paso5Tecnicos payload={state.payload} errores={state.errores} dispatch={dispatch} />
        </Seccion>

        <Seccion titulo="Usuarios">
          {/* Lotes: en curso (esta pantalla sólo abre Activa/Pausada), una sola puerta para las
              personas: el panel de lotes. Aquí, solo lectura. El servidor rechaza (422) otra lista;
              la que se reenvía al guardar es la misma y se ignora. */}
          <UsuariosEnLotes
            campanaId={id}
            onIrAlPanel={() => navigate(`/citas-tecnicas/campanas/${id}/resumen`)}
          />
        </Seccion>
      </div>

      {/* Modales */}
      <ModalImpacto
        abierto={!!impactoModal}
        impacto={impactoModal}
        guardando={guardando}
        onCancelar={() => setImpactoModal(null)}
        onConfirmar={confirmarImpacto}
      />

      {transicionModal && (
        <ModalTransicion
          estadoActual={estadoActual}
          nuevoEstado={transicionModal.nuevoEstado}
          loading={transicionLoading}
          error={transicionModal.error}
          onCancelar={() => !transicionLoading && setTransicionModal(null)}
          onConfirmar={ejecutarTransicion}
        />
      )}
    </div>
  );
}

// --- Subcomponentes ---

function Seccion({ titulo, children }) {
  return (
    <Card className="p-5">
      <h2 className="text-sm font-semibold text-slate-800 mb-4 pb-2 border-b border-slate-100">
        {titulo}
      </h2>
      {children}
    </Card>
  );
}

function NotaModoEdicion({ children }) {
  return (
    <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2 mb-4">
      {children}
    </div>
  );
}

const TRANSICIONES_UI = {
  Activa: [
    { estado: 'Pausada', label: 'Pausar', icon: <Pause className="w-3.5 h-3.5" /> },
    { estado: 'Completada', label: 'Completar', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
    { estado: 'Cancelada', label: 'Cancelar', icon: <XCircle className="w-3.5 h-3.5" />, variant: 'outline' },
  ],
  Pausada: [
    { estado: 'Activa', label: 'Reactivar', icon: <Play className="w-3.5 h-3.5" /> },
    { estado: 'Cancelada', label: 'Cancelar', icon: <XCircle className="w-3.5 h-3.5" />, variant: 'outline' },
  ],
};

function ModalTransicion({ estadoActual, nuevoEstado, loading, error, onCancelar, onConfirmar }) {
  const meta = {
    Pausada: {
      titulo: 'Pausar campaña',
      cuerpo: 'Los usuarios no podrán reservar nuevas citas mientras esté pausada. Las citas ya reservadas se mantienen.',
      btn: 'Pausar',
    },
    Activa: {
      titulo: 'Reactivar campaña',
      cuerpo: 'Los usuarios podrán volver a reservar citas. Se revalidará la coherencia de los datos.',
      btn: 'Reactivar',
    },
    Completada: {
      titulo: 'Marcar campaña como completada',
      cuerpo: 'Esta acción no se puede deshacer. La campaña pasará a estado terminal.',
      btn: 'Marcar completada',
    },
    Cancelada: {
      titulo: 'Cancelar campaña',
      cuerpo: 'Esta acción no se puede deshacer. Todas las citas pendientes y reservadas se cancelarán. Los usuarios y técnicos serán notificados en una fase posterior.',
      btn: 'Cancelar campaña',
    },
  }[nuevoEstado];

  if (!meta) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4"
      onClick={loading ? undefined : onCancelar}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-slate-900 mb-2">{meta.titulo}</h3>
        <p className="text-sm text-slate-600">{meta.cuerpo}</p>

        {error && (
          <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5">
            {error}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancelar}
            disabled={loading}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            disabled={loading}
            className={
              'px-3 py-1.5 text-sm rounded-lg text-white flex items-center gap-1.5 ' +
              (nuevoEstado === 'Cancelada'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-slate-900 hover:bg-slate-800') +
              ' disabled:opacity-60'
            }
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {meta.btn}
          </button>
        </div>
      </div>
    </div>
  );
}
